// src/components/version/VersionList.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { I18n } from 'aws-amplify';
import UserContext from './../../app/UserContext';
import { listAcsVersions, listV1, listV2 } from './api/Version';

import { Container, Row, Col, Table, Button, Spinner } from 'react-bootstrap';
import { SortDown, SortUp } from 'react-bootstrap-icons';
import { VersionFilter } from './VersionFilter';
import { VersionRecord } from './VersionRecord';


// 既存の SORT_DIRECTION を流用したい場合は import してください。
// ここでは単独でも動くようにローカル定義しています。
const SORT_DIRECTION = { ASC: 'ASC', DESC: 'DESC' };

// Version テーブルの列キー（UserList の USER.TABLE_COLUMN に相当）
const VERSION = {
  TABLE_COLUMN: {
    ID: 'id',
    ACTIVE: 'active',
    NO: 'no',
    VERSION: 'version',
    RELEASE_DATE: 'releaseDate',
    UPDATED_AT: 'updatedAt',
    CREATED_AT: 'createdAt',
  },
};

export const VersionList = () => {
  const PAGE_SIZE = 500;
  const { user } = useContext(UserContext);

  // 基本状態
  const [verLoad, setVerLoad] = useState(false);
  const [verData, setVerData] = useState([]);

  // 追加：v1/v2/すべて
  const [series, setSeries] = useState('all'); // 'all' | 'v1' | 'v2'

  // 既存：active=true を既定
  const [verFilter, setVerFilter] = useState({ active: true });

  const [verSort, setVerSort] = useState({
    key: VERSION.TABLE_COLUMN.RELEASE_DATE,  // releaseDate を既定ソートキー
    direction: SORT_DIRECTION.DESC
  });
  const [verNextToken, setVerNextToken] = useState(null);
  const [verNextRead, setVerNextRead] = useState(false);

  // 初回取得
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        if (isMounted) setVerLoad(true);
        const { items, nextToken } = await fetchPage(null, verFilter, series);
        if (isMounted) {
          setVerData(items);
          setVerNextToken(nextToken);
          setVerNextRead(false);
          setVerLoad(false);
        }
      } catch (e) {
        console.log(e);
        if (isMounted) setVerLoad(false);
      }
    })();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


    // フィルタ変更・series変更で再検索
  useEffect(() => {
    (async () => {
      setVerLoad(true);
      setVerData([]);
      try {
        const { items, nextToken } = await fetchPage(null, verFilter, series);
        setVerData(items);
        setVerNextToken(nextToken);
        setVerNextRead(false);
      } catch (e) {
        console.log(e);
      } finally {
        setVerLoad(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verFilter, series]);

    // 1ページ取得（series に応じてクエリ切替）
  const fetchPage = async (token, filter, seriesSel) => {
    if (seriesSel === 'v1') return await listV1({ active: filter.active, limit: PAGE_SIZE, nextToken: token });
    if (seriesSel === 'v2') return await listV2({ active: filter.active, limit: PAGE_SIZE, nextToken: token });
    // all：従来どおり listAcsVersions（version contains など他フィルタを使うなら拡張可）
    return await listAcsVersions({ active: filter.active }, token, PAGE_SIZE);
  };

  // 追加読み込み
  const addFetchVersions = async () => {
    setVerNextRead(true);
    try {
      const { items, nextToken } = await fetchPage(verNextToken, verFilter, series);
      setVerData(prev => [...prev, ...(items || [])]);
      setVerNextToken(nextToken ?? null);
    } catch (e) {
      console.log(e);
    } finally {
      setVerNextRead(false);
    }
  };

  // 取得データ→表示用整形（UserListの parseUser 相当）
  const parsedData = useMemo(() => {
    if (!Array.isArray(verData)) return [];
    return verData.map(v => ({
      [VERSION.TABLE_COLUMN.ID]: v.id,
      [VERSION.TABLE_COLUMN.ACTIVE]: v.active,
      [VERSION.TABLE_COLUMN.NO]: v.no,
      [VERSION.TABLE_COLUMN.VERSION]: v.version,
      [VERSION.TABLE_COLUMN.RELEASE_DATE]: v.releaseDate,
      [VERSION.TABLE_COLUMN.UPDATED_AT]: v.updatedAt,
      [VERSION.TABLE_COLUMN.CREATED_AT]: v.createdAt,
    }));
  }, [verData]);

  // ソート
  const versionSortable = (a, b) => {
    const key = verSort.key;
    const dir = verSort.direction;
    const av = a[key];
    const bv = b[key];

    if (av == null && bv == null) return (a.id || '').localeCompare(b.id || '');
    if (av == null) return dir === SORT_DIRECTION.ASC ? 1 : -1;
    if (bv == null) return dir === SORT_DIRECTION.ASC ? -1 : 1;

    if (key === VERSION.TABLE_COLUMN.NO) {
      const an = Number(av) || 0;
      const bn = Number(bv) || 0;
      if (an === bn) return (a.id || '').localeCompare(b.id || '');
      return dir === SORT_DIRECTION.ASC ? (an - bn) : (bn - an);
    }

    // 文字列/ISO日付: 'yyyy-mm-dd' は辞書順 = 日付順
    const as = typeof av === 'string' ? av : String(av);
    const bs = typeof bv === 'string' ? bv : String(bv);
    const cmp = as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
    if (cmp !== 0) return dir === SORT_DIRECTION.ASC ? cmp : -cmp;

    return (a.id || '').localeCompare(b.id || '');
  };

  const sortedData = useMemo(() => {
    const arr = Array.isArray(parsedData) ? [...parsedData] : [];
    arr.sort(versionSortable);
    return arr;
  }, [parsedData, verSort]); // eslint-disable-line react-hooks/exhaustive-deps

  // ソート切替
  const changeVerSort = (key, direction) => setVerSort({ key, direction });


  // ソートアイコン
  const resSortIcon = (argKey) => {
    if (verSort.key === argKey) {
      return verSort.direction === SORT_DIRECTION.ASC
        ? <SortUp style={{ cursor: 'pointer' }} size={12} onClick={() => changeVerSort(argKey, SORT_DIRECTION.DESC)} />
        : <SortDown style={{ cursor: 'pointer' }} size={12} onClick={() => changeVerSort(argKey, SORT_DIRECTION.ASC)} />;
    }
    return <SortDown style={{ cursor: 'pointer' }} color="gray" size={12} onClick={() => changeVerSort(argKey, SORT_DIRECTION.DESC)} />;
  };

  const noData = !Array.isArray(sortedData) || sortedData.length === 0;

  return (
    <>
      {/* Version List */}
      <Container className="main-content">
        <Row>
          <Col xs={8} md={8}>
            <h2>{I18n.get('title_versions') || 'バージョン一覧'}</h2>
          </Col>
          <Col xs={4} md={4}>
            {/* Todo: 追加ボタン*/}
            {/**
            <div className="text-end">
              <Button variant="secondary" disabled>
                {I18n.get('general_add') || '追加'}
              </Button>
            </div>
            */}
          </Col>
        </Row>

      {/* 既存の VersionFilter（active=true 既定） */}
      <VersionFilter
        filter={setVerFilter}
        nextToken={setVerNextToken}
        nextRead={setVerNextRead}
        load={verLoad}
        series={series}
        setSeries={setSeries}
      />

        <Row>
          <Col xs={12} md={12} className="mt-3">
            {verLoad ? (
              <div className="text-center">
                <Spinner as="span" animation="border" role="status" aria-hidden="true" />
              </div>
            ) : noData ? (
              <>{I18n.get('general_no_data') || 'データがありません'}</>
            ) : (
              <Table striped bordered hover responsive className="text-nowrap">
                <thead>
                  <tr>
                    <th>{I18n.get('general_status') || 'Status'} {resSortIcon(VERSION.TABLE_COLUMN.ACTIVE)}</th>
                    {/**
                    <th>No {resSortIcon(VERSION.TABLE_COLUMN.NO)}</th>
                    */}
                    <th>{I18n.get('table_column_version_version') || 'Version'} {resSortIcon(VERSION.TABLE_COLUMN.VERSION)}</th>
                    <th>{I18n.get('table_column_version_release_date') || 'Release Date'} {resSortIcon(VERSION.TABLE_COLUMN.RELEASE_DATE)}</th>
                    {/** 
                    <th>{I18n.get('general_latest_update') || 'Updated At'} {resSortIcon(VERSION.TABLE_COLUMN.UPDATED_AT)}</th>
                    <th>{I18n.get('general_create_date') || 'Created At'} {resSortIcon(VERSION.TABLE_COLUMN.CREATED_AT)}</th>
                    */}
                    </tr>
                </thead>

              <tbody>
                {sortedData.map((v, idx) => (
                  <VersionRecord key={v.id ?? `vr-${idx}`} version={v} index={idx} />
                ))}
              </tbody>
              {verNextToken ? (
                <tfoot>
                  <tr>
                    {verNextRead ? (
                      <td colSpan={8} className="text-center">
                        <Spinner as="span" animation="border" role="status" aria-hidden="true" />
                      </td>
                    ) : (
                      <td
                        colSpan={8}
                        className="text-center styleLiceRecd"
                        onClick={() => addFetchVersions()}
                      >
                        {I18n.get('general_paging') || 'さらに表示'}
                      </td>
                    )}
                  </tr>
                </tfoot>
              ) : null}
              </Table>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};
